import React, { lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useSearchParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  SessionExpiredPage,
  SessionEvent,
  armSessionTimeoutRedirectFromReason,
  getSessionExpiryPreNavigationDelayMs,
} from '@hsus-dev/session-policy';
import type { LogoutTriggeredDetail } from '@hsus-dev/session-policy';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import { SectionTilesProvider } from './context/SectionTilesContext';
import { ActiveRoleProvider } from './context/ActiveRoleContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { FormHeaderProvider } from './context/FormHeaderContext';
import { ThemeProvider } from './context/ThemeContext';
import { MsalRedirectHandler } from './components/MsalRedirectHandler/MsalRedirectHandler';
import { LoadingSpinner } from './components/Loading/LoadingSpinner';
import { LOADING_FORM_ARIA_LABEL, LOADING_FORM_MESSAGE } from './config/loadingUi.config';
import { ProtectedRoute } from './components/ProtectedRoute/ProtectedRoute';
import { ApiErrorNavigationRegistrar } from './components/ApiErrorNavigationRegistrar/ApiErrorNavigationRegistrar';
import {
  PortalPageHeader,
  PORTAL_PAGE_HEADER_HEIGHT_PX,
} from './components/PortalPageHeader/PortalPageHeader';
import {
  RouteScopedPortalFooter,
} from './components/RouteScopedPortalFooter/RouteScopedPortalFooter';
import { shouldShowRouteScopedPortalChrome } from './utils/routeScopedPortalChrome.utils';
import { AUTH_API_CONFIG } from './config/api.config';
import { DEFAULT_STALE_TIME_MS } from './config/time.config';
import { queryAllowsExamFormRouteEntry } from './utils/examFormQuery.utils';
import { SessionWarningModal } from './components/SessionWarningModal/SessionWarningModal';
import { HostPortalLoginRedirect } from './components/HostPortalLoginRedirect/HostPortalLoginRedirect';
import { SIGNED_OUT_ROUTE_PATH } from './constants/routeConstants';
import './styles/sessionExpiredPortalBridge.css';

const FormView = lazy(() => import('./pages/FormView/FormView').then((m) => ({ default: m.FormView })));
const ExamWorkspace = lazy(() => import('./pages/ExamWorkspace/ExamWorkspace').then((m) => ({ default: m.ExamWorkspace })));
const GenericErrorPage = lazy(() =>
  import('./pages/GenericErrorPage/GenericErrorPage').then((m) => ({ default: m.GenericErrorPage }))
);
const SignedOutPage = lazy(() =>
  import('./pages/SignedOutPage/SignedOutPage').then((m) => ({ default: m.SignedOutPage }))
);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 3,
      staleTime: DEFAULT_STALE_TIME_MS,
    },
  },
});

/** `/examform` entry: requires `eid` and `efid` query params (see `queryAllowsExamFormRouteEntry`). */
/** Shared loading fallback for route/lazy boundaries. */
const loadingFormSuspenseFallback = (
  <LoadingSpinner message={LOADING_FORM_MESSAGE} ariaLabel={LOADING_FORM_ARIA_LABEL} />
);

const ExamFormEntryRoute: React.FC = () => {
  const [searchParams] = useSearchParams();
  if (!queryAllowsExamFormRouteEntry(searchParams)) {
    return <Navigate to="/error" replace />;
  }
  return (
    <ProtectedRoute>
      {/* Nearest Suspense boundary so lazy FormView shows the shared loading fallback. */}
      <Suspense fallback={loadingFormSuspenseFallback}>
        <FormView />
      </Suspense>
    </ProtectedRoute>
  );
};

/** Standalone DBQ form (`/form/:formId`) — matches Healthcare Portal routes when using Form Engine Portal host. */
const FormEntryRoute: React.FC = () => (
  <ProtectedRoute>
    <Suspense fallback={loadingFormSuspenseFallback}>
      <FormView />
    </Suspense>
  </ProtectedRoute>
);

/** Legacy `/view` entry — `formId` / `documentId` from query (same as Healthcare Portal `FormView`). */
const ViewFormEntryRoute: React.FC = () => (
  <ProtectedRoute>
    <Suspense fallback={loadingFormSuspenseFallback}>
      <FormView />
    </Suspense>
  </ProtectedRoute>
);

/** Exam DBQ page (`/examform`) uses `FormPageHeader` only; hide global portal chrome and zero `--nav-height` for sticky offsets. */
const RouteScopedPortalChrome: React.FC = () => {
  const { pathname } = useLocation();
  const showPortalHeader = shouldShowRouteScopedPortalChrome(pathname);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--nav-height',
      showPortalHeader ? `${PORTAL_PAGE_HEADER_HEIGHT_PX}px` : '0px'
    );
  }, [showPortalHeader]);

  if (!showPortalHeader) return null;
  return <PortalPageHeader />;
};

export const App: React.FC = () => {
  const navigate = useNavigate();

  // Idle timeout → `/session-expired`. Absolute timeout → `/login` (HostPortalLoginRedirect → host portal).
  // User-initiated logout (`reason` = user | cross-tab) does not arm `sessionTimeoutNavigation`.
  //
  // WHY module-level navigation + armSessionTimeoutRedirectFromReason (see package README):
  // AuthContext.logout() clears storage in the same tick as unauthenticated state; `consumePendingSessionTimeoutRedirect`
  // in ProtectedRoute reconciles if navigate already updated the URL.
  useEffect(() => {
    let pendingNavigation: number | undefined;

    const handleLogout = (e: Event) => {
      const reason = (e as CustomEvent<LogoutTriggeredDetail>).detail?.reason;
      const path = armSessionTimeoutRedirectFromReason(reason);
      if (path !== null) {
        if (pendingNavigation !== undefined) {
          window.clearTimeout(pendingNavigation);
        }
        const delayMs = getSessionExpiryPreNavigationDelayMs();
        pendingNavigation = window.setTimeout(() => {
          pendingNavigation = undefined;
          navigate(path, { replace: true });
        }, delayMs);
      }
    };
    window.addEventListener(SessionEvent.LogoutTriggered, handleLogout);
    return () => {
      window.removeEventListener(SessionEvent.LogoutTriggered, handleLogout);
      if (pendingNavigation !== undefined) {
        window.clearTimeout(pendingNavigation);
      }
    };
  }, [navigate]);

  return (
  <AuthProvider>
    <ThemeProvider>
      <ApiErrorNavigationRegistrar />
      <SectionTilesProvider>
        <ActiveRoleProvider>
          <FormHeaderProvider>
            <QueryClientProvider client={queryClient}>
              <PermissionsProvider>
                <div className="portal-page-shell">
                <RouteScopedPortalChrome />
                {AUTH_API_CONFIG.providerType === 'msal' && (
                  <Suspense fallback={null}>
                    <MsalRedirectHandler />
                  </Suspense>
                )}
                <SessionWarningModal />
                <div className="portal-page-main flex min-h-0 flex-col">
                <Suspense fallback={loadingFormSuspenseFallback}>
                  <Routes>
                    <Route
                      path="/session-expired"
                      element={
                        <div className="fep-session-expired-bridge">
                          <SessionExpiredPage portalName="Form Engine" showSignInAgain={false} />
                        </div>
                      }
                    />
                    <Route path="/login" element={<HostPortalLoginRedirect />} />
                    <Route
                      path={SIGNED_OUT_ROUTE_PATH}
                      element={
                        <Suspense fallback={loadingFormSuspenseFallback}>
                          <SignedOutPage />
                        </Suspense>
                      }
                    />
                    <Route path="/examform" element={<ExamFormEntryRoute />} />
                    <Route path="/form/:formId" element={<FormEntryRoute />} />
                    <Route path="/view" element={<ViewFormEntryRoute />} />
                    <Route
                      path="/exam-workspace"
                      element={
                        <ProtectedRoute>
                          <Suspense fallback={loadingFormSuspenseFallback}>
                            <ExamWorkspace />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/exam-workspace/:examId"
                      element={
                        <ProtectedRoute>
                          <Suspense fallback={loadingFormSuspenseFallback}>
                            <ExamWorkspace />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/error"
                      element={
                        <ProtectedRoute requireAuth={false}>
                          <Suspense fallback={loadingFormSuspenseFallback}>
                            <GenericErrorPage />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/" element={<Navigate to="/error" replace />} />
                    <Route path="*" element={<Navigate to="/error" replace />} />
                  </Routes>
                </Suspense>
                </div>
                <RouteScopedPortalFooter />
                </div>
              </PermissionsProvider>
            </QueryClientProvider>
          </FormHeaderProvider>
        </ActiveRoleProvider>
      </SectionTilesProvider>
    </ThemeProvider>
  </AuthProvider>
  );
};
